import { useState } from "react";
import { Helmet } from "react-helmet";
import { usePlaceholder } from "../context/usePlaceholder";
import { buildFAQData } from "./utils/seoSchema";

const programList = {
  "Eigene Anfrage": ["ich habe eine Frage oder Idee, die hier nicht gelistet ist"],
  "2D": ["Adobe Illustrator", "Photoshop", "AfterEffects"],
  "3D": ["Blender", "Cinema 4D", "Rendering", "Scripting Python"],
  "CODE": ["React JS", "Angular", "Next.js", "Three.js"],
  "MEDIA": ["Videoschnitt Premiere", "Videobearbeitung AfterEffects", "FinalCut"],
  "Web Entwicklung": ["interaktives Web App", "eigene Webentwicklung"],
};

const inputClass =
  "flex-1 bg-transparent text-sm font-thin text-[#d0cbb8] placeholder-[#d0cbb8]/60 <placeholder-text></placeholder-text> outline-none border-none ring-0 focus:outline-none focus:ring-0 focus:border-none caret-[#AC8E66] font-mono";

function CustomCourseRequest({
  onClose,
  initialStep,
  initialCategory,
  initialProgram,
}) {
  const allowedStartSteps = new Set(["kategorie", "programm", "form", "danke"]);
  const startingStep = allowedStartSteps.has(initialStep) ? initialStep : "kategorie";
  const [step, setStep] = useState(startingStep);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || null);
  const [selectedProgram, setSelectedProgram] = useState(initialProgram || null);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [consentGiven, setConsentGiven] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const { nameSinglePlaceholder } = usePlaceholder();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

  const emailHasError = emailTouched && formData.email.trim() !== "" && !isValidEmail(formData.email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentGiven) return;
    if (!isValidEmail(formData.email)) return;
    const payload = {
      category: selectedCategory,
      program: selectedProgram,
      program2: selectedProgram,
      ...formData,
    };
    try {
      const response = await fetch("https://www.denisbitter.de/stage01/api/CustomCourseRequest.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) setStep("danke");
      else console.error("Anfrage fehlgeschlagen");
    } catch (error) {
      console.error("Fehler beim Senden der Anfrage:", error);
    }
  };

  const stepLabel = { kategorie: "1/3", programm: "2/3", form: "3/3", danke: "" };

  let content = null;

  if (step === "intro") {
    content = (
      <>
        <p className="text-sm text-[#d0cbb8]/80 mb-2">
          <span className="text-[#AC8E66]">$</span> request --help
        </p>
        <p className="text-xs text-[#d0cbb8]/80 mb-1">
          <span className="text-[#AC8E66]/80">&gt;</span> du stehst mittendrin in einem projekt, {nameSinglePlaceholder}?
        </p>
        <p className="text-xs text-[#d0cbb8]/80 mb-4">
          <span className="text-[#AC8E66]/80">&gt;</span> sag mir was dir fehlt.
        </p>
        <button
          onClick={() => setStep("kategorie")}
          className="w-full py-2 text-xs text-[#d0cbb8]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer font-mono"
        >
          <span className="text-[#AC8E66]">$</span> start --anfrage
        </button>
      </>
    );
  } else if (step === "kategorie") {
    content = (
      <>
        <p className="text-sm text-[#d0cbb8]/80 mb-2">
          <span className="text-[#AC8E66]">$</span> request --kategorie
        </p>
        <p className="text-xs text-[#d0cbb8]/80 mb-4">
          <span className="text-[#AC8E66]/80">&gt;</span> wobei brauchst du unterstuetzung?
        </p>
        <div className="flex flex-col gap-1.5">
          {Object.keys(programList).map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                if (cat === "Eigene Anfrage") {
                  setSelectedProgram(programList[cat][0] || "eigene anfrage");
                  setStep("form");
                  return;
                }
                setSelectedProgram(null);
                setStep("programm");
              }}
              className="w-full flex items-center gap-3 py-2 px-1 border-b border-[#AC8E66]/10 last:border-b-0 hover:bg-[#AC8E66]/5 transition-colors text-left cursor-pointer group"
            >
              <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
              <span className="text-sm text-[#d0cbb8]/60 group-hover:text-[#d0cbb8] transition-colors">{cat.toLowerCase()}</span>
              <span className="ml-auto text-xs text-[#AC8E66]/30 group-hover:text-[#AC8E66]/80 transition-colors">[select]</span>
            </button>
          ))}
        </div>
      </>
    );
  } else if (step === "programm") {
    content = (
      <>
        <p className="text-sm text-[#d0cbb8]/80 mb-2">
          <span className="text-[#AC8E66]">$</span> select --programm
        </p>
        <p className="text-xs text-green-400/70 mb-1">
          <span className="text-green-400/40">&gt;</span> kategorie: {selectedCategory.toLowerCase()}
        </p>
        <p className="text-xs text-[#d0cbb8]/80 mb-4">
          <span className="text-[#AC8E66]/80">&gt;</span> welches programm?
        </p>
        <div className="flex flex-col gap-1.5">
          {programList[selectedCategory].map((prog) => (
            <button
              key={prog}
              onClick={() => { setSelectedProgram(prog); setStep("form"); }}
              className="w-full flex items-center gap-3 py-2 px-1 border-b border-[#AC8E66]/10 last:border-b-0 hover:bg-[#AC8E66]/5 transition-colors text-left cursor-pointer group"
            >
              <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
              <span className="text-sm text-[#d0cbb8]/60 group-hover:text-[#d0cbb8] transition-colors">{prog}</span>
              <span className="ml-auto text-xs text-[#AC8E66]/30 group-hover:text-[#AC8E66]/80 transition-colors">[select]</span>
            </button>
          ))}
        </div>
        <button
          onClick={() => setStep("kategorie")}
          className="w-full py-1.5 text-xs text-[#d0cbb8]/30 hover:text-[#d0cbb8]/80 transition-colors cursor-pointer font-mono mt-3 pt-3 border-t border-[#AC8E66]/10"
        >
          <span className="text-[#AC8E66]/30">[</span>zurueck<span className="text-[#AC8E66]/30">]</span>
        </button>
      </>
    );
  } else if (step === "form") {
    content = (
      <form onSubmit={handleSubmit}>
        <p className="text-sm text-[#d0cbb8]/80 mb-2">
          <span className="text-[#AC8E66]">$</span> request --send
        </p>
        <p className="text-xs text-green-400/70 mb-1">
          <span className="text-green-400/40">&gt;</span> {selectedCategory?.toLowerCase()} → {selectedProgram}
        </p>
        <p className="text-xs text-[#d0cbb8]/80 mt-2">
          <span className="text-[#AC8E66]/80">&gt;</span> Schreibe ein paar Zeilen...
        </p>

        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
          <span className="text-xs text-[#d0cbb8]/80 w-14 shrink-0">name</span>
          <input
            type="text"
            name="name"
            placeholder={nameSinglePlaceholder || "dein name"}
            value={formData.name}
            onChange={handleInputChange}
            required
            className={inputClass}
          />
        </div>

        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
          <span className="text-xs text-[#d0cbb8]/80 w-14 shrink-0">email</span>
          <input
            type="email"
            name="email"
            placeholder="deine@email.de"
            value={formData.email}
            onChange={(e) => {
              handleInputChange(e);
              if (!emailTouched) setEmailTouched(true);
            }}
            onBlur={() => setEmailTouched(true)}
            required
            aria-invalid={emailHasError}
            className={`${inputClass} ${emailHasError ? "text-red-500" : ""}`}
          />
        </div>
        {emailHasError && (
          <p className="text-xs text-red-500 mt-1 ml-6">
            <span className="text-red-300/70">&gt;</span> bitte gib eine gueltige e-mail ein.
          </p>
        )}

        <div className="flex gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs shrink-0 pt-1">&gt;</span>
          <span className="text-xs text-[#d0cbb8]/80 w-14 shrink-0 pt-1">message</span>
          <textarea
            name="message"
            placeholder="details zu deinem projekt..."
            value={formData.message}
            onChange={handleInputChange}
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </div>

        <div className="flex items-center gap-2 py-3">
          <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
          <span id="custom-course-request-consent-label" className="text-xs text-[#d0cbb8]/80">datenschutz</span>
          <label htmlFor="custom-course-request-consent" className="inline-flex items-center cursor-pointer ml-auto">
            <input
              id="custom-course-request-consent"
              type="checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="sr-only"
              required
              aria-labelledby="custom-course-request-consent-label"
            />
            <div className={`w-8 h-4 rounded-full relative transition ${consentGiven ? "bg-green-500/30" : "bg-[#d0cbb8]/10"}`}>
              <div className={`w-3 h-3 rounded-full absolute top-0.5 left-0.5 transition-transform ${consentGiven ? "translate-x-4 bg-green-400" : "bg-[#d0cbb8]/80"}`} />
            </div>
          </label>
        </div>
        <p className="text-xs text-[#d0cbb8]/80 ml-6 -mt-1 mb-3">
          gemaess{" "}
          <a href="/datenschutz" className="underline text-[#AC8E66]/80 hover:text-[#AC8E66]" target="_blank" rel="noopener noreferrer">
            datenschutz
          </a>
        </p>

        <div className="flex flex-col gap-2 pt-3 border-t border-[#AC8E66]/10">
          <button
            type="submit"
            disabled={!consentGiven}
            className={`w-full py-2 text-xs text-[#d0cbb8]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors font-mono ${
              !consentGiven ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <span className="text-[#AC8E66]">$</span> send --anfrage
          </button>
          <button
            type="button"
            onClick={() => {
              setFormData({ name: "", email: "", message: "" });
              setConsentGiven(false);
              setEmailTouched(false);
              setSelectedProgram(null);
              setSelectedCategory(null);
              setStep("kategorie");
            }}
            className="w-full py-1.5 text-xs text-[#d0cbb8]/30 hover:text-[#d0cbb8]/80 transition-colors cursor-pointer font-mono"
          >
            <span className="text-[#AC8E66]/30">[</span>zurueck<span className="text-[#AC8E66]/30">]</span>
          </button>
        </div>
      </form>
    );
  } else if (step === "danke") {
    content = (
      <>
        <p className="text-sm text-[#d0cbb8]/80 mb-2">
          <span className="text-[#AC8E66]">$</span> request --status
        </p>
        <p className="text-sm text-green-400">
          <span className="text-green-400/60">&gt;</span> anfrage gesendet.
        </p>
        <p className="text-sm text-[#d0cbb8]/60 mt-1">
          <span className="text-[#AC8E66]/80">&gt;</span> danke{formData.name ? `, ${formData.name}` : ""}. denis meldet sich innerhalb eines tages.
        </p>
        <p className="text-sm text-[#d0cbb8]/80 mt-1">
          <span className="text-[#AC8E66]/80">&gt;</span> oder schneller:{" "}
          <a href="tel:+4915153231791" className="text-[#AC8E66] hover:underline">0151 5323 1791</a>
        </p>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(buildFAQData(programList))}
        </script>
      </Helmet>
      <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden 
      shadow-xl shadow-[#1a1a1a]/20 font-mono mt-4 w-4xl">
        {/* Title Bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
          {typeof onClose === "function" ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Anfrage schliessen"
              className="w-3 h-3 rounded-full bg-[#ff5f57] hover:brightness-110 cursor-pointer"
            />
          ) : (
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          )}
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-xs text-[#AC8E66] select-none">anfrage</span>
          {stepLabel[step] && (
            <span className="ml-auto text-xs text-[#d0cbb8]/80 select-none">{stepLabel[step]}</span>
          )}
        </div>
        {/* Content */}
        <div className="px-4 sm:px-6 py-5 bg-[#151515]">
          {content}
        </div>
      </div>
    </>
  );
}

export default CustomCourseRequest;
